<div [ngClass]="{'modal-content': isModal, 'page-content': !isModal}">
  <!-- Tab Navigation (for both modal and page layouts) -->
  @if (showHeader) {
    <div class="auth-tabs">
      <button 
        [class.active]="currentMode() === AuthMode.LOGIN"
        (click)="switchMode(AuthMode.LOGIN)"
        type="button" 
        class="auth-tab-button">
        Einloggen
      </button>
      <button 
        [class.active]="currentMode() === AuthMode.SIGNUP"
        (click)="switchMode(AuthMode.SIGNUP)"
        type="button" 
        class="auth-tab-button">
        Registrieren
      </button>
      @if (isModal) {
        <button type="button" class="close-button" (click)="onCancel()">×</button>
      }
    </div>
  }

  <!-- Social Login -->
  <div class="social-login">
    <button type="button" 
            class="google-btn" 
            [loading]="isGoogleSubmitting()"
            [disabled]="isSubmitting()"
            (click)="loginWithGoogle()">
      <span class="google-icon">
        <svg viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      </span>
      <span class="btn-text">Mit Google anmelden</span>
    </button>
    
    <button type="button" 
            class="apple-btn" 
            [loading]="isAppleSubmitting()"
            [disabled]="isSubmitting()"
            (click)="loginWithApple()">
      <span class="apple-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
        </svg>
      </span>
      <span class="btn-text">Mit Apple anmelden</span>
    </button>
  </div>

  <!-- Divider -->
  <div class="auth-divider">
    <span class="divider-text">oder</span>
  </div>

  <!-- Secondary Authentication: Email Form Section -->
  <div class="secondary-auth-section">
    <div class="section-header secondary">
      <h4 class="section-title secondary">{{ currentMode() === AuthMode.SIGNUP ? 'Konto mit E-Mail erstellen' : 'Mit E-Mail fortfahren' }}</h4>
    </div>

    <!-- Login Form -->
    @if (currentMode() === AuthMode.LOGIN) {
      <form [formGroup]="loginForm" (ngSubmit)="onLoginSubmit()" class="auth-form email-form">
        <div class="form-group">
          <label for="login-email" [class.sr-only]="isModal">E-Mail</label>
          <input 
            type="email" 
            id="login-email" 
            formControlName="email" 
            class="form-control" 
            autocomplete="email"
            [class.is-invalid]="loginEmail?.invalid && (loginEmail?.dirty || loginEmail?.touched)"
            placeholder="E-Mail-Adresse"
          >
          @if (loginEmail?.invalid && (loginEmail?.dirty || loginEmail?.touched)) {
            <div class="error-message">
              @if (loginEmail?.errors?.['required']) { <div>E-Mail ist erforderlich.</div> }
              @if (loginEmail?.errors?.['email']) { <div>Bitte geben Sie eine gültige E-Mail-Adresse ein.</div> }
            </div>
          }
        </div>
        
        <div class="form-group">
          <label for="login-password" [class.sr-only]="isModal">Passwort</label>
          <input 
            type="password" 
            id="login-password" 
            formControlName="password"
            class="form-control"
            autocomplete="current-password"
            [class.is-invalid]="loginPassword?.invalid && (loginPassword?.dirty || loginPassword?.touched)"
            placeholder="Passwort"
          >
          @if (loginPassword?.invalid && (loginPassword?.dirty || loginPassword?.touched)) {
            <div class="error-message">
              @if (loginPassword?.errors?.['required']) { <div>Passwort ist erforderlich.</div> }
            </div>
          }
        </div>
        
        @if (rememberMe) {
          <div class="form-group remember-me">
            <app-toggle-switch
              label="Angemeldet bleiben"
              [checked]="loginForm.get('rememberMe')?.value"
              (checkedChange)="loginForm.get('rememberMe')?.setValue($event)">
            </app-toggle-switch>
          </div>
        }
        
        @if (errorMessage()) {
          <div class="error-alert">
            {{ errorMessage() }}
          </div>
        }
        
        <div class="form-actions">
          @if (isModal) {
            <button type="button" class="btn btn-outline" (click)="onCancel()">Abbrechen</button>
          }
          <button 
            type="submit" 
            class="btn btn-primary btn-full" 
            [loading]="isEmailSubmitting()"
            [disabled]="loginForm.invalid || isSubmitting()"
          >
            <span class="btn-text">Mit E-Mail anmelden</span>
          </button>
        </div>
        
        @if (!isModal && !showHeader) {
          <div class="auth-link-container">
            <span>Haben Sie noch kein Konto?</span>
            <button type="button" class="auth-link" (click)="switchMode(AuthMode.SIGNUP)">Registrieren</button>
          </div>
        }
      </form>
    }
    
    <!-- Signup Form -->
    @if (currentMode() === AuthMode.SIGNUP) {
      <form [formGroup]="signupForm" (ngSubmit)="onSignupSubmit()" class="auth-form email-form">
        <div class="form-group">
          <label for="signup-first-name" [class.sr-only]="isModal">Vorname</label>
          <input
            type="text"
            id="signup-first-name"
            formControlName="firstName"
            class="form-control"
            autocomplete="given-name"
            maxlength="100"
            [class.is-invalid]="signupFirstName?.invalid && (signupFirstName?.dirty || signupFirstName?.touched)"
            placeholder="Vorname"
          >
          @if (signupFirstName?.invalid && (signupFirstName?.dirty || signupFirstName?.touched)) {
            <div class="error-message">
              @if (signupFirstName?.errors?.['required']) { <div>Vorname ist erforderlich.</div> }
            </div>
          }
        </div>

        <div class="form-group">
          <label for="signup-last-name" [class.sr-only]="isModal">Nachname</label>
          <input
            type="text"
            id="signup-last-name"
            formControlName="lastName"
            class="form-control"
            autocomplete="family-name"
            maxlength="100"
            [class.is-invalid]="signupLastName?.invalid && (signupLastName?.dirty || signupLastName?.touched)"
            placeholder="Nachname"
          >
          @if (signupLastName?.invalid && (signupLastName?.dirty || signupLastName?.touched)) {
            <div class="error-message">
              @if (signupLastName?.errors?.['required']) { <div>Nachname ist erforderlich.</div> }
            </div>
          }
        </div>
        
        <div class="form-group">
          <label for="signup-email" [class.sr-only]="isModal">E-Mail</label>
          <input
            type="email"
            id="signup-email"
            formControlName="email"
            class="form-control"
            autocomplete="email"
            [class.is-invalid]="signupEmail?.invalid && (signupEmail?.dirty || signupEmail?.touched)"
            placeholder="E-Mail-Adresse"
          >
          @if (signupEmail?.invalid && (signupEmail?.dirty || signupEmail?.touched)) {
            <div class="error-message">
              @if (signupEmail?.errors?.['required']) { <div>E-Mail ist erforderlich.</div> }
              @if (signupEmail?.errors?.['email']) { <div>Bitte geben Sie eine gültige E-Mail-Adresse ein.</div> }
            </div>
          }
        </div>
        
        <div class="form-group">
          <label for="signup-password" [class.sr-only]="isModal">Passwort</label>
          <input
            type="password"
            id="signup-password"
            formControlName="password"
            class="form-control"
            autocomplete="new-password"
            [class.is-invalid]="signupPassword?.invalid && (signupPassword?.dirty || signupPassword?.touched)"
            placeholder="Passwort"
          >
          @if (signupPassword?.invalid && (signupPassword?.dirty || signupPassword?.touched)) {
            <div class="error-message">
              @if (signupPassword?.errors?.['required']) { <div>Passwort ist erforderlich.</div> }
              @if (signupPassword?.errors?.['minlength']) { <div>Passwort muss mindestens 8 Zeichen lang sein.</div> }
              @if (signupPassword?.errors?.['pattern']) { <div>Passwort muss Großbuchstaben, Kleinbuchstaben und Zahlen oder Sonderzeichen enthalten.</div> }
            </div>
          }
        </div>
        
        <div class="form-group">
          <label for="signup-confirm-password" [class.sr-only]="isModal">Passwort bestätigen</label>
          <input 
            type="password" 
            id="signup-confirm-password" 
            formControlName="confirmPassword"
            class="form-control"
            autocomplete="new-password"
            [class.is-invalid]="signupConfirmPassword?.invalid && (signupConfirmPassword?.dirty || signupConfirmPassword?.touched)"
            placeholder="Passwort bestätigen"
          >
          @if (signupConfirmPassword?.invalid && (signupConfirmPassword?.dirty || signupConfirmPassword?.touched)) {
            <div class="error-message">
              @if (signupConfirmPassword?.errors?.['required']) { <div>Bitte bestätigen Sie Ihr Passwort.</div> }
              @if (signupConfirmPassword?.errors?.['mismatch']) { <div>Passwörter stimmen nicht überein.</div> }
            </div>
          }
        </div>
        
        @if (errorMessage()) {
          <div class="error-alert">
            {{ errorMessage() }}
          </div>
        }
        
        <div class="form-actions">
          @if (isModal) {
            <button type="button" class="btn btn-outline" (click)="onCancel()">Abbrechen</button>
          }
          <button 
            type="submit" 
            class="btn btn-primary btn-full" 
            [loading]="isEmailSubmitting()"
            [disabled]="signupForm.invalid || isSubmitting()"
          >
            <span class="btn-text">Mit E-Mail registrieren</span>
          </button>
        </div>
        
        @if (!isModal && !showHeader) {
          <div class="auth-link-container">
            <span>Haben Sie bereits ein Konto?</span>
            <button type="button" class="auth-link" (click)="switchMode(AuthMode.LOGIN)">Anmelden</button>
          </div>
        }
      </form>
    }
  </div>
</div>